import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEOHead from "@/components/base/SEOHead";
import Breadcrumb from "@/components/base/Breadcrumb";
import { articles } from "@/mocks/data";

export default function ArticleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();

  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <div className="py-20 text-center">
        <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
          <i className="ri-file-unknow-line text-3xl text-foreground-300"></i>
        </div>
        <p className="text-foreground-500 mb-6">{t("article.noArticles")}</p>
        <Link
          to="/article"
          className="inline-flex px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          {t("article.backToList")}
        </Link>
      </div>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    author: {
      "@type": "Person",
      name: article.author,
    },
    datePublished: article.publishedAt,
    publisher: {
      "@type": "Organization",
      name: "快猫视频",
    },
    url: `https://www.kuaimaovideo.com/article/${article.id}`,
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: t("breadcrumb.home"), item: "https://www.kuaimaovideo.com/" },
      { "@type": "ListItem", position: 2, name: t("article.title"), item: "https://www.kuaimaovideo.com/article" },
      { "@type": "ListItem", position: 3, name: article.title },
    ],
  };

  const relatedArticles = articles.filter((a) => a.id !== article.id).slice(0, 3);

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(",")}
        ogType="article"
        ogImage={article.coverImage}
        schema={{ ...articleSchema, ...breadcrumbSchema }}
      />
      <Breadcrumb
        items={[
          { label: t("article.title"), path: "/article" },
          { label: article.title },
        ]}
      />

      <article className="py-8 md:py-12 bg-background-50">
        <div className="container-page max-w-4xl">
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 whitespace-nowrap">
                {article.category}
              </span>
              <span className="text-sm text-foreground-400">
                {t("article.publishedAt")}: {article.publishedAt}
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 leading-snug">
              {article.title}
            </h1>
            <div className="mt-4 flex items-center gap-3">
              <img
                src={article.authorAvatar}
                alt={article.author}
                className="w-8 h-8 rounded-full object-cover"
              />
              <span className="text-sm text-foreground-600">{article.author}</span>
              <span className="text-sm text-foreground-400">
                · {article.readCount} {t("article.readCount")}
              </span>
            </div>
          </div>

          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full rounded-xl mb-10 object-cover aspect-[2/1]"
          />

          <div
            className="prose prose-lg max-w-none prose-headings:text-foreground-900 prose-p:text-foreground-700 prose-p:leading-relaxed prose-h2:text-xl prose-h2:font-heading prose-h2:mt-10 prose-h2:mb-4 prose-p:my-4 prose-strong:text-foreground-900"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-10 pt-6 border-t border-background-200">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500 mr-2">{t("article.tags")}:</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700 whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <div className="mt-12">
              <h3 className="text-xl font-heading text-foreground-900 mb-6">
                {t("article.related")}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-3">
                      <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors">
                        {ra.title}
                      </h4>
                      <p className="mt-1 text-xs text-foreground-400">{ra.publishedAt}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8">
            <Link
              to="/article"
              className="inline-flex items-center gap-1.5 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              {t("article.backToList")}
            </Link>
          </div>
        </div>
      </article>
    </>
  );
}